import React from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import NumberField from 'Components/DesignSystem/NumberField/NumberField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import RadioGroup from 'Components/DesignSystem/RadioGroup/RadioGroup';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Typography from 'Components/DesignSystem/Typography/Typography';
import ColourField from './ColourField';
import ChipsInput from './ChipsInput';
import ResolveAssetField from './ResolveAssetField';
import ResolveCheckboxList from './ResolveCheckboxList';
import ResolveCompositeRow from './ResolveCompositeRow';
import { ManifestChildSetting, ManifestOptionValue, ManifestOptions, ManifestSetting } from '../manifest/types';
import { TEXTAREA_MAX_LENGTH, TEXT_MAX_LENGTH, intBoundOf } from '../manifest/valueUtils';

export interface ResolveControlRendererProps {
  setting: ManifestSetting | ManifestChildSetting;
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
  error?: string | null;
  testId: string;
  /** `''` (default) for a top-level row — `ConfigFieldRow` already shows the label to its
   * right. A composite child passes its own `title` so the control shows it inline,
   * matching the pre-existing `widget_frame`/`tappable_options` layout. */
  label?: string;
  /** Shrinks a `number` control the way the original `widget_frame` composite did
   * (`tw-max-w-[140px]`, `steppers={false}`) — set by `ResolveCompositeRow` for its
   * children, never for a top-level row. */
  compact?: boolean;
}

const normalizeOptions = (options?: ManifestOptions): ManifestOptionValue[] =>
  (options?.values ?? []).map((option) => (typeof option === 'object' ? option : { value: option, label: String(option) }));

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asStringArray = (value: unknown): string[] => (Array.isArray(value) ? (value as string[]) : []);
const asNumberOrNull = (value: unknown): number | null => (typeof value === 'number' ? value : null);

/** Controls whose own component shows `error` inline. Every other control gets the message
 * underneath (`<testId>-error`), so a row that blocks Save always says why. */
const SHOWS_OWN_ERROR = new Set(['text', 'textarea', 'colour', 'chips']);

const renderControl = ({
  setting,
  value,
  onChange,
  disabled = false,
  error = null,
  testId,
  label = '',
  compact = false,
}: ResolveControlRendererProps): React.ReactElement | null => {
  switch (setting.control) {
    case 'text':
      return (
        <TextField
          label={label}
          placeholder={setting.title}
          value={asString(value)}
          onChange={onChange}
          maxLength={TEXT_MAX_LENGTH}
          required={'required' in setting && Boolean(setting.required)}
          error={Boolean(error)}
          helperText={error ?? undefined}
          disabled={disabled}
          data-testid={testId}
        />
      );

    case 'textarea':
      return (
        <Textarea
          label={label}
          placeholder={setting.title}
          value={asString(value)}
          onChange={onChange}
          maxLength={TEXTAREA_MAX_LENGTH}
          error={Boolean(error)}
          helperText={error ?? undefined}
          disabled={disabled}
          rows={3}
          data-testid={testId}
        />
      );

    case 'number':
      return (
        <NumberField
          label={label}
          value={asNumberOrNull(value)}
          onChange={onChange}
          unit={setting.unit}
          steppers={!compact}
          disabled={disabled}
          className={compact ? 'tw-max-w-[140px]' : undefined}
          data-testid={testId}
        />
      );

    case 'colour':
      return (
        <ColourField
          testId={testId}
          ariaLabel={setting.title}
          value={asString(value)}
          onChange={onChange}
          error={error}
          disabled={disabled}
        />
      );

    case 'asset':
      return (
        <ResolveAssetField
          testId={testId}
          value={asString(value)}
          onChange={onChange}
          disabled={disabled}
          error={Boolean(error)}
        />
      );

    case 'toggle':
      return (
        <Toggle label={label} checked={Boolean(value)} onChange={onChange} disabled={disabled} data-testid={testId} />
      );

    case 'radio':
      return (
        <RadioGroup
          label={label}
          orientation="horizontal"
          options={normalizeOptions(setting.options)}
          value={value as string | number}
          onChange={onChange}
          disabled={disabled}
          data-testid={testId}
        />
      );

    case 'dropdown':
      return (
        <Dropdown
          label={label}
          options={normalizeOptions(setting.options)}
          value={value as string | number}
          onChange={onChange}
          disabled={disabled}
          data-testid={testId}
        />
      );

    case 'chips':
      return (
        <ChipsInput
          testId={testId}
          value={asStringArray(value)}
          onChange={onChange}
          extendable={Boolean(setting.options?.extendable)}
          min={intBoundOf(setting.options?.min)}
          max={intBoundOf(setting.options?.max)}
          error={error}
          disabled={disabled}
        />
      );

    case 'checkbox':
    case 'multi_select':
      return (
        <ResolveCheckboxList
          testId={testId}
          value={(Array.isArray(value) ? value : []) as Array<string | number>}
          options={setting.options?.values ?? []}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'composite':
      return (
        <ResolveCompositeRow
          // Only a top-level setting ever carries `children` — composites never nest.
          setting={setting as ManifestSetting}
          value={(value as Record<string, unknown>) ?? {}}
          onChange={onChange}
          disabled={disabled}
          testId={testId}
        />
      );

    default:
      return null;
  }
};

/**
 * `switch (setting.control)` — the one place a manifest control type maps to a rendered
 * DS component. Every setting/section in the Resolve Configurations screen goes through
 * this dispatch; no setting name is ever special-cased here (the one true exception,
 * `product_card_id`, never reaches this component — `ResolveConfigPage` excludes the
 * `product_card` section from the generic loop entirely). See `okf/resolve/e2e.md`.
 */
const ResolveControlRenderer: React.FC<ResolveControlRendererProps> = ({
  setting,
  value,
  onChange,
  disabled = false,
  error = null,
  testId,
  label = '',
  compact = false,
}) => {
  const control = renderControl({ setting, value, onChange, disabled, error, testId, label, compact });
  if (!error || SHOWS_OWN_ERROR.has(setting.control)) return control;
  return (
    <>
      {control}
      <Typography variant="caption-metadata" className="tw-text-tone-danger-ink" data-testid={`${testId}-error`}>
        {error}
      </Typography>
    </>
  );
};

export default ResolveControlRenderer;
